import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { LocalizationKeys } from 'assets/localizations/localizationKeys';
import { observer } from 'mobx-react';
import React from 'react';
import { useStores } from 'stores/storeInitializer';

// Same values as the recipient type of the wire and of the legacy form, so no mapping is needed.
export type Journey = 'BUSINESS' | 'INDIVIDUAL';

const ALL_JOURNEYS: Journey[] = ['BUSINESS', 'INDIVIDUAL'];

interface JourneySwitchProps {
    value: Journey;
    onChange: (journey: Journey) => void;
    options?: Journey[];
    disabled?: boolean; // Locked, e.g. while a submit is in flight.
}

/** Two clear buttons (Company / Personal) with exactly one always selected. */
export const JourneySwitch: React.FC<JourneySwitchProps> = observer(
    ({ value, onChange, options = ALL_JOURNEYS, disabled = false }) => {
        const { store } = useStores();

        return (
            <ToggleButtonGroup
                exclusive
                fullWidth
                color="primary"
                value={value}
                disabled={disabled}
                aria-label={store.L(LocalizationKeys.BillingDetailsRecipientType)}
                onChange={(_event, next: Journey | null) => {
                    // Clicking the selected button reports null: never leave the switch without a selection.
                    if (next !== null) {
                        onChange(next);
                    }
                }}
            >
                {options.map((journey) => (
                    <ToggleButton
                        key={journey}
                        value={journey}
                        sx={{
                            minHeight: 44,
                            textTransform: 'none',
                            fontWeight: journey === value ? 700 : 400,
                            '&.Mui-selected, &.Mui-selected:hover': {
                                bgcolor: 'primary.main',
                                color: 'primary.contrastText',
                            },
                        }}
                    >
                        {store.L(
                            journey === 'BUSINESS'
                                ? LocalizationKeys.BillingDetailsRecipientBusiness
                                : LocalizationKeys.BillingDetailsRecipientIndividual,
                        )}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
        );
    },
);

export default JourneySwitch;
